import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Seo — declarative <head> manager for public pages.
 *
 * Props
 *   title             — full <title>
 *   description       — meta description (155–160 chars)
 *   canonical         — absolute canonical URL (recommended)
 *   lang              — page language ("en" | "es")
 *   hreflangAlternates — [{ hreflang, href }] for bilingual pages
 *   ogImage           — absolute image URL for social previews
 *   ogType            — default "website"
 *   noindex           — set true for utility pages
 *   jsonLd            — single JSON-LD object OR array of objects
 */
export default function Seo({
  title,
  description,
  canonical,
  lang = "en",
  hreflangAlternates = [],
  ogImage = "https://yosimediastudio.com/og-cover.jpg",
  ogType = "website",
  noindex = false,
  jsonLd,
}) {
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet htmlAttributes={{ lang }}>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* hreflang alternates for bilingual pages */}
      {hreflangAlternates.map((alt) => (
        <link key={`${alt.hreflang}-${alt.href}`} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}

      {/* Open Graph */}
      {title ? <meta property="og:title" content={title} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Yosi Media Studio" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === "es" ? "es_US" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
